import { DataIoService } from '../services/data-io.service';
import { ConfigService } from '../services/config.service';
import { SimulatorConfig } from './SimulatorConfig';
import { SimulatorModuleConfig } from './SimulatorModuleConfig';
import { MenuLogic } from './MenuLogic';
import { MenuNode } from './MenuNode';

export class MenuLogicSnippets {
    _debug: boolean = false;

    debug(msg: string): void {
        if (this._debug) {
            console.log("MenuLogicSnippets: " + msg);
        }
    }

    config: ConfigService;
    ventConfig: SimulatorModuleConfig;
    solarConfig: SimulatorModuleConfig;
    heatpumpConfig: SimulatorModuleConfig;
    boilerConfig: SimulatorModuleConfig;
    bufferCylinderConfig: SimulatorModuleConfig;
    ioService: DataIoService;
    static _instance: MenuLogicSnippets;

    static run(ioService: DataIoService) {
        MenuLogicSnippets._instance = null;
        MenuLogicSnippets._instance = new MenuLogicSnippets(ioService);
    }
    constructor(ioService: DataIoService) {
        this.ioService = ioService;
        this.config = this.ioService.getConfiguration();
        this.debug("Instantiating.");
        this.ventConfig = this.config.simulatorConfig.getModuleConfigByName('ventilation');
        this.solarConfig = this.config.simulatorConfig.getModuleConfigByName('solarSystem');
        this.heatpumpConfig = this.config.simulatorConfig.getModuleConfigByName('heatPump');
        this.bufferCylinderConfig = this.config.simulatorConfig.getModuleConfigByName('bufferCylinder');
        this.boilerConfig = this.config.simulatorConfig.getModuleConfigByName('boiler');
        console.log("Loading config for country: " + this.config.simulatorConfig.getCountry());
        MenuLogic.clear();
        this.preFlight();
        this.applyVentilationFilters();
        this.applySolarSystemFilters();
        this.applyHeatpumpFilters();
        this.applyBoilerFilters();
        this.applyMiscFilters();
        this.applyHeatingCircuitFilters();
        this.installerConditions();
        this.vrc720fConditions();
        this.aiConditions();
        console.log("MenuLogicSnippets ran.");
    }
    disable(id: string) {
        MenuLogic.addCriteria(id, () => { return false; });
    }
    enable(id: string) {
        MenuLogic.addCriteria(id, () => { return true; });
    }
    aiConditions() {

        let sc = this.config.simulatorConfig;

        this.ioService.getDeviceInfo().getScale('d340').setCurrentValue(sc.hasControl() ? 0 : 1)

        let isStandalone = () => sc.isStandalone(); //
        let hasControl = () => (this.ioService.getDeviceInfo().getScale('d340').getCurrentElement() == 'AICI_YES_NORMAL');
        for (let hcId of this.ioService.deviceInfo().getHeatingCircuitIds()) {
            let hc = this.ioService.deviceInfo().getHeatingCircuitById(hcId);
            hc.getScale('heizkurve').setCurrentValue(hasControl() ? 1.2 : 0.6);
            if (!hasControl()) {
                hc.getScale('max_vorlauf_soll_temperatur').setCurrentValue(55);
            }
        }
        this.disable('zeitprogrammassistent_ohne_temperatur');
        let hasControlFn = () => this.ioService.getDeviceInfo().getScale('d340').getCurrentElement() != 'AICI_YES_NORMAL'
        MenuLogic.addCriteria('fh.anlagenkonfiguration', hasControlFn);
        MenuLogic.addCriteria('anchor.estrichttrocknung', hasControlFn);
        ['datum', 'zeit', 'sommerzeit', 'korrekturwert'].forEach((s: string) => {
            MenuLogic.addCriteria(`einstellungen.${s}`, () => !hasControl());
        });
        MenuLogic.addCriteria('wizard_set_date', () => !hasControl());
        MenuLogic.addCriteria('wizard_set_time', () => !hasControl());

        MenuLogic.addCriteria('wizard-brine-fill-program', () => this.ioService.deviceInfo().getScale('d133').getCurrentElement() == 'DELIGHT_HP_DIA_CODE_ID_133_VALUE_1');
        MenuLogic.addCriteria('diagnoseCode.521', () => this.ioService.deviceInfo().getScale('d133').getCurrentElement() == 'DELIGHT_HP_DIA_CODE_ID_133_VALUE_1');
        MenuLogic.addCriteria('diagnoseCode.132', () => this.ioService.deviceInfo().getScale('d133').getCurrentElement() == 'DELIGHT_HP_DIA_CODE_ID_133_VALUE_1');
        MenuLogic.addCriteria('wizard-brine-airvent-program', () => this.ioService.deviceInfo().getScale('d133').getCurrentElement() == 'DELIGHT_HP_DIA_CODE_ID_133_VALUE_1');
        MenuLogic.addCriteria('pp_brfl', () => this.ioService.deviceInfo().getScale('d133').getCurrentElement() == 'DELIGHT_HP_DIA_CODE_ID_133_VALUE_1');
        MenuLogic.addCriteria('pp_zwkr', () => this.ioService.deviceInfo().getScale('d133').getCurrentElement() == 'DELIGHT_HP_DIA_CODE_ID_133_VALUE_1');
        MenuLogic.addCriteria('pp_allkr', () => this.ioService.deviceInfo().getScale('d133').getCurrentElement() == 'DELIGHT_HP_DIA_CODE_ID_133_VALUE_1');
        MenuLogic.addCriteria('wizard-brine-brinevent-program', () => this.ioService.deviceInfo().getScale('d133').getCurrentElement() == 'DELIGHT_HP_DIA_CODE_ID_133_VALUE_1');

        MenuLogic.addCriteria('zone.1', isStandalone);
        MenuLogic.addCriteria('regelung.abwesenheit', isStandalone);
        MenuLogic.addCriteria('kuehlen_fuer_einige_tage', isStandalone);
        MenuLogic.addCriteria('dhw.modus', isStandalone);
        MenuLogic.addCriteria('dhw.weekly_planner', isStandalone);
        MenuLogic.addCriteria('dhw.weekly_planner_circulation', isStandalone);
        MenuLogic.addCriteria('regelung.warmwasser.schnell', isStandalone);
        MenuLogic.addCriteria('lueftung.menu.anchor', isStandalone);
        MenuLogic.addCriteria('stosslueften', isStandalone);
        MenuLogic.addCriteria('feuchteschutz', isStandalone);
        MenuLogic.addCriteria('zeitprogrammassistent', isStandalone);
        MenuLogic.addCriteria('regelung.system_off', isStandalone);
        MenuLogic.addCriteria('wizard_fsf_active', () => this.ioService.deviceInfo().getScale('d602').getCurrentElement() == 'DELIGHT_HP_DIA_CODE_ID_602_VALUE_1');
        MenuLogic.addCriteria('wizard_fsf_inactive', () => this.ioService.deviceInfo().getScale('d602').getCurrentElement() == 'DELIGHT_HP_DIA_CODE_ID_602_VALUE_0');
        MenuLogic.addCriteria('config_min_vorlauftemperatur_kuehlen.1', () => {
            return this.ioService.getDeviceInfo().getHeatingCircuit(0).getRawValue('kuehlen_moeglich') == 'AICI_YES_NORMAL';
        });
        MenuLogic.addCriteria('dauerhaftes.kühlen.menuitem', () => {
            console.log(this.config.simulatorConfig.isStandalone() ? "standalone" : "not standalone");
            console.log("kuehlen möglich: " + this.ioService.getDeviceInfo().getHeatingCircuit(0).getRawValue('kuehlen_moeglich'));
            console.log("d127: " + this.ioService.getDeviceInfo().getScale('d127').getCurrentElement());
            return this.config.simulatorConfig.isStandalone() && this.ioService.getDeviceInfo().getHeatingCircuit(0).getRawValue('kuehlen_moeglich') == 'AICI_YES_NORMAL' && this.ioService.getDeviceInfo().getScale('d127').getCurrentElement() == 'DELIGHT_HP_DIA_CODE_ID_127_VALUE_1';
        });
        let d126Ext = (node: MenuNode) => {
            return this.ioService.deviceInfo().getScale('d126').getCurrentValue() != 0;
        }
        MenuLogic.addCriteria('dc600-699', () => this.ioService.deviceInfo()?.getScale('last_code')?.getCurrentValue() == 19);
        let d126Affected = ['testProgramP27', 'diagnoseCode.073', 'diagnoseCode.076', 'dataOverview.076', 'diagnoseCode.368', 'dataOverview.368', 'diagnoseCode.369', 'dataOverview.369', 'diagnoseCode.362', 'dataOverview.362',];
        for (let affected of d126Affected) {
            MenuLogic.addCriteria(affected, d126Ext);
        }
        MenuLogic.addCriteria('diagnoseCode.395', (n) => !d126Ext(n));
        MenuLogic.addCriteria('wizard_odu_missing', () => this.config.isTemporaryStartup());
        let standAloneDCodes = ['004', '007', '043', '071', '097', '130', "134", "135", "136", "137", "138", "139", "140", "141", "142", "143", "144", "145", "146", "147", "148", "149", "150", "151", "152", "153", "154", "155", "156", "157", "158", "159", "160", "161", "162", "163", "342", "346", "347", "348", "349", "351", "352", "353", "355", "357", "362", "384", "385", "386", "387", "388", "389", "390"];
        for (let code of standAloneDCodes) {
            MenuLogic.addCriteria('diagnoseCode.' + code, () => {
                return this.config.simulatorConfig.isStandalone();
            });
        }
        MenuLogic.addCriteria('dataOverview.007', () => isStandalone());
        MenuLogic.addCriteria('dataOverview.004', () => isStandalone());

        MenuLogic.addCriteria('menuEntryActuatorTest127', () => {
            return this.ioService.deviceInfo().getScale('d126').getCurrentValue() == 0;
        });
    }
    vrc720fConditions() {

        MenuLogic.addCriteria('anchor-config-radio-connection', () => {
            return this.config.simulatorConfig.hasRadio();
        });
        MenuLogic.addCriteria('wizard_empfangsstaerke_sensor', () => {
            return this.config.simulatorConfig.hasRadio();
        });
        MenuLogic.addCriteria('wizard_empfangsstaerke_regler', () => {
            return this.config.simulatorConfig.hasRadio();
        });

        MenuLogic.addCriteria('zonenzuordnung_fernbedienung_3', () => {
            return this.config.simulatorConfig.isVariant('vrc720');
        })
        MenuLogic.addCriteria('zonenzuordnung_fernbedienung_4', () => {
            return this.config.simulatorConfig.isVariant('vrc720');
        })

    }
    installerConditions() {

        let installer_condition_1 = () => {
            return this.ioService.deviceInfo().getRawValue('remote_connected') == 'WIZARD_REMOTE_CONTROLS_OPTION_ENUM1';
        }

        MenuLogic.addCriteria('confirm_remote', installer_condition_1);
        let installer_condition_2 = (node: MenuNode) => {
            let hasSufficientHeatingCircuitCount = (this.config.simulatorConfig.heatingCircuitCount() >= 2);
            let hasBufferCylinder = (this.config.simulatorConfig.hasBufferCylinder());
            let hasSolarBuffer = (this.config.simulatorConfig.hasSolar());
            return hasSolarBuffer || hasSufficientHeatingCircuitCount || hasSolarBuffer;
        }
        let installer_condition_2_1 = (node: MenuNode) => {
            let hasSufficientHeatingCircuitCount = (this.config.simulatorConfig.heatingCircuitCount() >= 2);
            let hasBufferCylinder = (this.config.simulatorConfig.hasBufferCylinder());
            let hasSolarBuffer = (this.config.simulatorConfig.hasSolar());
            return hasSolarBuffer || hasSufficientHeatingCircuitCount || hasSolarBuffer || this.config.simulatorConfig.hasVR7x();
        }
        MenuLogic.addCriteria('installer_solar_buffer_options', installer_condition_2);
        MenuLogic.addCriteria('installer_warning', installer_condition_2_1);
        MenuLogic.addCriteria('wizard-device-list', installer_condition_2);
        let vr71config_setter = () => {
            this.ioService.deviceInfo().getHooks().setInstallerVR71Config();
            return true;
        }

        MenuLogic.addCriteria('wizard-device-list', vr71config_setter);


        let installer_condition_3 = () => {
            let hasSufficientHeatingCircuitCount = (this.config.simulatorConfig.heatingCircuitCount() > 2);
            let hasAllStor = this.config.simulatorConfig.hasAllStor();
            let hasSolar = this.config.simulatorConfig.hasSolar();
            let di = this.ioService.deviceInfo();
            let solarOption = di.getRawValue('wizard_solar_option');
            let solarSettingsCondition = ((solarOption != 'WIZARD_SOLAR_OPTIONS_ENUM1') && (solarOption != 'WIZARD_SOLAR_OPTIONS_ENUM2'));
            let enabled = ((hasSufficientHeatingCircuitCount || hasSolar || hasAllStor) && (solarSettingsCondition))
            return enabled;
        }
        let onlyHeatpumpCondition = () => {
            if (this.config.simulatorConfig.hasHeatpump() && !this.config.simulatorConfig.hasVR7x()) {
                return false;
            }
            return true;
        }
        MenuLogic.addCriteria('zone_option_3', onlyHeatpumpCondition);
        MenuLogic.addCriteria('zone_option_4', onlyHeatpumpCondition);
        MenuLogic.addCriteria('zone_option_5', onlyHeatpumpCondition);

        MenuLogic.addCriteria('installer_solar_buffer_count', installer_condition_3);

        let installer_condition_4 = () => {
            //console.log("Evaluating installer_condition_4");
            let hasBufferStorage = this.config.simulatorConfig.hasBufferCylinder();
            let hasSufficientHeatingCircuitCount = (this.config.simulatorConfig.heatingCircuitCount() == 2);
            let systemSchemaCodeCondition = this.ioService.deviceInfo().getScale('systemschema_code').getCurrentValue() == 8;
            /*
            console.log("Bufferstorage: "+( (hasSufficientHeatingCircuitCount)?"yes":"no"));
            console.log("HasSufficient: "+( (hasBufferStorage)?"yes":"no"));
            console.log("syscode: "+( (systemSchemaCodeCondition)?"yes":"no"));
            console.log("VR70Count: "+this.config.simulatorConfig.getVR70Count());
            console.log("VR71Count: "+this.config.simulatorConfig.getVR71Count());
            */
            if (this.hasHeatpump() && this.config.simulatorConfig.getVR71Count() == 0) {
                return true;
            }
            return (hasBufferStorage || hasSufficientHeatingCircuitCount || systemSchemaCodeCondition) && (this.config.simulatorConfig.getVR70Count() > 0 && this.config.simulatorConfig.getVR71Count() == 0);
        }
        MenuLogic.addCriteria('installer_zone_options', installer_condition_4);

        let installer_condition_5 = () => {
            let hasBufferStorage = this.config.simulatorConfig.hasBufferCylinder();
            let hasSufficientHeatingCircuitCount = (this.config.simulatorConfig.heatingCircuitCount() == 2);

            let val = (hasBufferStorage || hasSufficientHeatingCircuitCount) && (this.config.simulatorConfig.getVR70Count() > 0 && this.config.simulatorConfig.getVR71Count() == 0);

            return (hasBufferStorage || hasSufficientHeatingCircuitCount) && (this.config.simulatorConfig.getVR70Count() > 0 && this.config.simulatorConfig.getVR71Count() == 0);
        }

        MenuLogic.addCriteria('wizard_mavr70_options', installer_condition_5);

        let installer_condition_6 = () => {
            let hasSufficientHeatingCircuitCount = (this.config.simulatorConfig.heatingCircuitCount() > 2);
            let hasAllStor = this.config.simulatorConfig.hasAllStor();
            let hasSolar = this.config.simulatorConfig.hasSolar();
            return (hasSufficientHeatingCircuitCount || hasAllStor || hasSolar) && (this.config.simulatorConfig.hasVR71());
        }

        MenuLogic.addCriteria('wizard_heatingcircuit_count', installer_condition_6);
        MenuLogic.addCriteria('wizard_mavr71_options', installer_condition_6);
        let installer_condition_7 = () => {
            return this.config.simulatorConfig.hasHeatpump() && this.ioService.deviceInfo().getScale('systemschema_code').getCurrentValue() >= 8;
        }

        MenuLogic.addCriteria('wizard_ma2_option', installer_condition_7);
        let installer_condition_8 = () => {
            // implemented by menu
        }


        let onlyBoilers = () => {
            let config = this.config.simulatorConfig;
            let hasHeatGenerator = config.hasBoiler();
            let hasSomethingElse = config.solarCylinderCount() || config.hasBufferCylinder() || config.hasHeatpump() || config.heatingCircuitCount() > 1; // ignore ventilation
            if (hasHeatGenerator && !hasSomethingElse) {
                return false;
            }
            return true;
        };
        MenuLogic.addCriteria('wizard_systemschema_code', onlyBoilers)
    }


    preFlight() {

        /**
            Wenn eine Wärmepumpe und ein Heizgerät gewählt sind Systemschema-Code „8“, wenn mehrere Wärmepumpen und ein Heizgerät gewählt sind Systemschema-Code „16“.
        **/
        let boilerCount = this.boilerCount();
        let heatpumpCount = this.heatpumpCount();
        let systemCode = 0;
        if (boilerCount > 0 && heatpumpCount == 0) {
            systemCode = 1;
        }
        if (boilerCount == 1 && heatpumpCount == 1) {
            systemCode = 8;
        }
        if (heatpumpCount > 1) {
            if (this.config.simulatorConfig.showInstallation()) {
                systemCode = 8;
            } else {
                systemCode = 16;

            }
        }


        /**
            nur ventilation
        **/
        if (this.config.simulatorConfig.justVentilation()) {
            let di = this.ioService.deviceInfo();
            let dz = di.getDefaultZone();
            let dh = di.getDefaultHeatingCircuit();
            dz.setValue('zone_active', 'ENUM_ZONE_ENABLED_NO');
            dh.setValue('circuitType', 'DELIGHT_GREEN_IQ_INACTIVE');
            dz.setValue('warmwasserspeicher', 'DELIGHT_GREEN_IQ_INACTIVE');
            let justVentilationCriteria = () => { return !this.config.simulatorConfig.justVentilation() };

            MenuLogic.addCriteria('info.wasserdruck', justVentilationCriteria);
            MenuLogic.addCriteria('config-zone-1', justVentilationCriteria);
            MenuLogic.addCriteria('kreis-1-entry', justVentilationCriteria);
            MenuLogic.addCriteria('brennerzustand', justVentilationCriteria);
            MenuLogic.addCriteria('anchor-config-estrichttrocknungsprofil', justVentilationCriteria);
            MenuLogic.addCriteria('anchor.estrichttrocknung', justVentilationCriteria);
            MenuLogic.addCriteria('info.vorlauftemperatur-anlage', justVentilationCriteria);

            this.ioService.deviceInfo().getScale('systemschema_code').setCurrentValue(1);
        }




        this.ioService.deviceInfo().getScale('solar_buffer_count').setCurrentValue(this.config.simulatorConfig.solarCylinderCount());

        if (systemCode > 0) {
            this.ioService.deviceInfo().getScale('systemschema_code').setCurrentValue(systemCode);
        }
        if (this.hasHeatpump()) {

            for (let zoneId of this.ioService.deviceInfo().getZoneIds()) {
                let zone = this.ioService.deviceInfo().getZoneById(zoneId);
                zone.getSensor('temperature', 'water_nominal').setCurrentValue(55);
            }

            for (let hcId of this.ioService.deviceInfo().getHeatingCircuitIds()) {
                let hc = this.ioService.deviceInfo().getHeatingCircuitById(hcId);
                hc.getScale('warmwassertemperatur').setCurrentValue(55);
                hc.getScale('max_vorlauf_soll_temperatur').setCurrentValue(55);
                hc.getScale('heizkurve').setCurrentValue(0.6);
            }
        } else {

            for (let zoneId of this.ioService.deviceInfo().getZoneIds()) {
                let zone = this.ioService.deviceInfo().getZoneById(zoneId);
                //zone.getSensor('temperature', 'water_nominal').setCurrentValue(60);
            }

            for (let hcId of this.ioService.deviceInfo().getHeatingCircuitIds()) {
                let hc = this.ioService.deviceInfo().getHeatingCircuitById(hcId);
                hc.getScale('warmwassertemperatur').setCurrentValue(60);
                hc.getScale('heizkurve').setCurrentValue(1.2);
            }
        }

        if (this.config.simulatorConfig.solarCylinderCount() == 1) {
            this.ioService.deviceInfo().getScale('configuration_vr71').setCurrentValue(2);
        }

        if (this.config.simulatorConfig.solarCylinderCount() == 2) {
            this.ioService.deviceInfo().getScale('configuration_vr71').setCurrentValue(1);
        }

        if (this.config.simulatorConfig.hasAllStor() && this.config.simulatorConfig.hasVR71()) {
            this.ioService.deviceInfo().getScale('configuration_vr71').setCurrentValue(6);
        }

        if (!this.config.simulatorConfig.showInstallation()) {
            this.ioService.deviceInfo().setValue('wizard_help_shown', 'true');
        }
        this.ioService.deviceInfo().getHooks().setDefaultVR71Config();

        let activeHeatingCircuits = this.config.simulatorConfig.activeHeatingCircuits();
        let availableHeatingCircuits = this.config.simulatorConfig.heatingCircuitCount();
        let ids = this.ioService.deviceInfo().getHeatingCircuitIds();
        for (let id of ids) {
            let hc = this.ioService.deviceInfo().getHeatingCircuitById(id);
            if (id > activeHeatingCircuits) {
                hc.setValue('circuitType', 'DELIGHT_GREEN_IQ_INACTIVE');
            }
        }

    }
    applyHeatingCircuitFilters() {
        let condition_12_2 = () => {
            return this.isMixedCircuit();
        };
        MenuLogic.addCriteria('status.mixer', condition_12_2);
        MenuLogic.addCriteria('vorlaufisttemp', condition_12_2);
    }
    applyHeatpumpFilters() {

        //MenuLogic.addCriteria("energy_consumption_cooling",()=>{return this.config.simulatorConfig.hasHeatpump();});
        if (false) {

            // das hier alles wird ausgeblendet, wenn keine wärmepumpe konfiguriert wurde.    
            let heatpumpInfluences = [
                'kuehlen_fuer_einige_tage',
                'umweltertrag',
                'tarife',
                'konfiguration-wp-regelungsmodul',
                'fluesterbetrieb',
                'feuchteschutz',
                'wp-regelungsmodul',
                'kuehlen_bei_aussentemperatur',
                'quellenregenerierung',
                'hybridmanager',
                'bivalenzpunkt_heizen',
                'bivalenzpunkt_warmwasser',
                'alternativpunkt',
                'temperatur_notbetrieb',
                'evu',
                'zusatzheizgeraet',
                'zusatzheizgeraet_typ',
                'offset_pufferspeicher',
                'automatisch_kuehlen',
                'aktueller_taupunkt',
                'hysterese.speicherladung'
            ]
            let heatpumpPerHC = [
                'kuehlen.',
                'config_kuehlen_moeglich.',
                'config_taupunktueberwachung.',
                'config_min_vorlauftemperatur_kuehlen.',
                'config_offset_taupunkt.'
            ]
            for (let i of this.ioService.deviceInfo().getHeatingCircuitIds()) {
                for (let id of heatpumpPerHC) {
                    heatpumpInfluences.push(id + i.toString());
                }
            }
            for (let identifier of heatpumpInfluences) {
                MenuLogic.addCriteria(identifier, () => { return false; });
            }
            MenuLogic.addCriteria('brennerzustand', () => { return true; });
            MenuLogic.addCriteria('aussentemp_gemittelt', () => { return false; });

        }
        for (let id of this.ioService.deviceInfo().getHeatingCircuitIds()) {
            MenuLogic.addCriteria('externe-waermeanforderung-' + id.toString(), () => {
                return (this.config.simulatorConfig.hasVR7x());
            });
        }
        if (this.hasHeatpump()) {
            MenuLogic.addCriteria('brennerzustand', () => { return false; });
            MenuLogic.addCriteria('aussentemp_gemittelt', () => { return true; });

            // das hier alles wird ausgeblendet, wenn keine wärmepumpe konfiguriert wurde.    
            let heatingCircuitIds = this.ioService.deviceInfo().getHeatingCircuitIds();
            for (let id of heatingCircuitIds) {
                MenuLogic.addCriteria('kuehlen.' + id.toString(), () => {
                    return (this.ioService.deviceInfo().getHeatingCircuitById(id).getRawValue('kuehlen_moeglich') == 'ENUM_COOLING_ALLOWED_YES');
                });
                MenuLogic.addCriteria('config_min_vorlauftemperatur_kuehlen.' + id.toString(), () => {
                    return this.ioService.deviceInfo().getHeatingCircuitById(id).getRawValue('kuehlen_moeglich') == 'ENUM_COOLING_ALLOWED_YES';
                });
            }
            MenuLogic.addCriteria('bivalenzpunkt_heizen', () => {
                return (this.ioService.deviceInfo().getRawValue('hybridmanager') != 'ENUM_HYBRID_MANAGER_TRIVAI');
            });


            MenuLogic.addCriteria('kuehlen_fuer_einige_tage', () => {
                return (this.ioService.deviceInfo().getDefaultHeatingCircuit().getRawValue('kuehlen_moeglich') == 'ENUM_COOLING_ALLOWED_YES');
            });

            MenuLogic.addCriteria('feuchteschutz', () => {
                return (this.ioService.deviceInfo().getRawValue('configuration_wp_ma2') == 'ENUM_HP_MODUL_OUTPUT_2_DEHUMIDIFIER');
            });

            MenuLogic.addCriteria('tarife', () => {
                return (this.ioService.deviceInfo().getRawValue('hybridmanager') == 'ENUM_HYBRID_MANAGER_TRIVAI');
            });
            MenuLogic.addCriteria('zusatzheizgeraet_typ', () => {
                return (this.ioService.deviceInfo().getRawValue('hybridmanager') == 'ENUM_HYBRID_MANAGER_TRIVAI');
            });
            MenuLogic.addCriteria('aktueller_taupunkt', () => {
                return (
                    this.ioService.deviceInfo().getDefaultHeatingCircuit().getRawValue('kuehlen_moeglich') == 'ENUM_COOLING_ALLOWED_YES') &&
                    (
                        (this.ioService.deviceInfo().getDefaultHeatingCircuit().getRawValue('raumaufschaltung') == "DELIGHT_GREEN_IQ_ACTIVE" ||
                            (this.ioService.deviceInfo().getDefaultHeatingCircuit().getRawValue('raumaufschaltung') == "ENUM_ROOM_TEMPERATURE_INFLUENCE_THERMOSTAT")
                        )
                    );
            });
            for (let hc of this.ioService.deviceInfo().getHeatingCircuitIds()) {
                let taupunkt_condition = () => {
                    return (
                        this.ioService.deviceInfo().getDefaultHeatingCircuit().getRawValue('kuehlen_moeglich') == 'ENUM_COOLING_ALLOWED_YES') &&
                        (
                            (this.ioService.deviceInfo().getDefaultHeatingCircuit().getRawValue('raumaufschaltung') == "DELIGHT_GREEN_IQ_ACTIVE" ||
                                (this.ioService.deviceInfo().getDefaultHeatingCircuit().getRawValue('raumaufschaltung') == "ENUM_ROOM_TEMPERATURE_INFLUENCE_THERMOSTAT")
                            )
                        );
                };
                MenuLogic.addCriteria('config_offset_taupunkt.' + hc.toString(), taupunkt_condition);
                MenuLogic.addCriteria('config_taupunktueberwachung.' + hc.toString(), taupunkt_condition);
            }
        }
    }



    applyBufferCylinderFilters() {
    }


    applyBoilerFilters() {
        /*
        if(!(this.boilerConfig.isAvailable() && this.boilerConfig.getValue()=="ecoTecExclusive")){
            MenuLogic.addCriteria('brennstoffverbrauch',()=>{return false;});
        }*/
    }
    hasBoiler() {
        return this.config.simulatorConfig.hasBoiler();
    }
    hasSolar() {
        return this.config.simulatorConfig.hasSolar();
    }
    allStorConfigured() {
        return (this.config.simulatorConfig.hasVR70() && this.ioService.deviceInfo().vr70() == 3);
    }
    solarConfigured() {
        return (this.config.simulatorConfig.hasVR70() && [6, 12].includes(this.ioService.deviceInfo().vr70()));
    }
    hasAllStor() {
        return this.config.simulatorConfig.hasAllStor();
    }
    hasHeatpump() {
        return this.config.simulatorConfig.hasHeatpump();
    }
    boilerCount() {
        return this.config.simulatorConfig.boilerCount();
    }
    heatpumpCount() {
        return this.config.simulatorConfig.heatpumpCount();
    }

    heatingCircuitCount() {
        return this.config.simulatorConfig.heatingCircuitCount();
    }
    applyMiscFilters() {
        let vc = this.ventConfig;
        let hpc = this.heatpumpConfig;
        let bcc = this.bufferCylinderConfig;
        let condition_hasEnabledChilds = (node: MenuNode) => {

            for (let child of node.getChilds()) {
                for (let subChild of child.getChilds()) {
                    if (subChild.isEnabled()) {
                        return true;
                    }
                }
            }
            return false;
        };

        MenuLogic.addCriteria('anchor.energiedaten', condition_hasEnabledChilds);
        MenuLogic.addCriteria('regelung.durchregler', (node) => {
            return !this.config.simulatorConfig.isStandalone();
        });
        MenuLogic.addCriteria('anchor.config.buffercylinder', (node) => { return (condition_hasEnabledChilds(node)); });
        MenuLogic.addCriteria('regelung.warmwasser', () => {
            return this.config.simulatorConfig.isStandalone();
            //return this.ioService.deviceInfo().getDefaultZone().getRawValue('warmwasserspeicher')=='DELIGHT_GREEN_IQ_ACTIVE';
        });

        MenuLogic.addCriteria('regelung.warmwasser.schnell', () => {
            return this.ioService.deviceInfo().getDefaultZone().getRawValue('warmwasserspeicher') == 'DELIGHT_GREEN_IQ_ACTIVE';
        });

        MenuLogic.addCriteria('ansteuerumkehr', () => { return this.heatpumpCount() > 1 || this.boilerCount() > 1; });
        MenuLogic.addCriteria('ansteuerreihenfolge', () => { return this.heatpumpCount() > 1 || this.boilerCount() > 1; });

        MenuLogic.addCriteria('config_vr70', () => { return this.config.simulatorConfig.hasVR70(); });
        MenuLogic.addCriteria('config_ma_vr70', () => { return this.config.simulatorConfig.hasVR70(); });
        MenuLogic.addCriteria('DELIGHT_INSTALLATION_ASSISTANT_SUBTITLE_COOLING_TECHNOLOGY', () => !this.config.isTemporaryStartup());
        MenuLogic.addCriteria('temporary_startup', () => this.config.isTemporaryStartup());
        MenuLogic.addCriteria('DELIGHT_INSTALLATION_ASSISTANT_CURRENT_LIMITATION_SUBTITLE', () => !this.config.isTemporaryStartup());
        MenuLogic.addCriteria('config_vr71', () => { return this.config.simulatorConfig.hasVR71(); });
        MenuLogic.addCriteria('config_ma_vr71', () => { return this.config.simulatorConfig.hasVR71(); });
        MenuLogic.addCriteria('sensor-aktor-test-menu', () => { return this.config.simulatorConfig.hasVR7x(); })
        MenuLogic.addCriteria('sensor-aktor-test-hook', () => { return this.config.simulatorConfig.hasVR7x(); })
        MenuLogic.addCriteria('konf.ext.eingaenge', () => { return this.config.simulatorConfig.hasVR7x(); })
        // 12.8
        let ids_12_8 = [
            'config.warmwasser.speicherladepumpe',
            'config.warmwasser.speicherladung_offset',
            'config.warmwasser.max_speicherladezeit',
            'config.warmwasser.sperrzeit_speicherladung'
        ];
        let condition_12_8 = () => {
            let sc = this.ioService.deviceInfo().getScale('systemschema_code').getCurrentValue();

            if ([2, 10, 11, 12, 13, 16].includes(sc)) {
                return true;
            }

            if (sc == 1 && (this.hasSolar() || this.hasAllStor() || this.allStorConfigured() || this.solarConfigured())) {
                return true;
            }
            if ((sc == 8) && (this.heatpumpCount() > 0) && (this.boilerCount() == 0)) {
                return true;
            }
            return false;

        }
        for (let id of ids_12_8) {
            MenuLogic.addCriteria(id, condition_12_8);
        }
        // 12.9
        let condition_12_9 = () => {
            let di = this.ioService.deviceInfo();
            return (this.config.simulatorConfig.hasVR70() && di.vr70() == 1) || (this.config.simulatorConfig.hasVR71() && di.vr71() == 3 && [1, 2, 8, 9, 10, 11, 12, 13, 16].includes(di.system_scheme()));
        };
        MenuLogic.addCriteria('buffer_top', condition_12_9);
        MenuLogic.addCriteria('buffer_bottom', condition_12_9);
        // 12.10        
        let condition_12_10 = () => {
            let di = this.ioService.deviceInfo();
            return di.vr71() == 6 || di.vr70() == 3;
        };
        MenuLogic.addCriteria('buffer_top_dhw', condition_12_10);
        MenuLogic.addCriteria('buffer_bottom_dhw', condition_12_10);
        MenuLogic.addCriteria('buffer_top_ch', condition_12_10);
        MenuLogic.addCriteria('buffer_bottom_ch', condition_12_10);
        MenuLogic.addCriteria('max_ww_inlet_nominal', condition_12_10);
        MenuLogic.addCriteria('buffer_solar_1_temp', condition_12_10);
        // 12.11
        let condition_12_11 = () => {
            /*
            console.log("solar buffer count: "+this.ioService.deviceInfo().getScale('solar_buffer_count'));
            console.log("VR71Config: "+this.ioService.deviceInfo().vr71());
            */
            let di = this.ioService.deviceInfo();
            return ((di.vr70() == 3) || (di.vr71() == 6)) && di.getScale('solar_buffer_count').getCurrentValue() == 2;
        };
        MenuLogic.addCriteria('solarbuffer_2_bottom', condition_12_11);
        MenuLogic.addCriteria('buffer_solar_2_maxtemp', condition_12_11);
        for (let hcId of this.ioService.deviceInfo().getHeatingCircuitIds()) {
            let zoneActiveCondition = () => {
                return this.ioService.deviceInfo().getHeatingCircuitIds().includes(hcId) && (this.ioService.deviceInfo().getHeatingCircuitById(hcId).getRawValue('circuitType') != "DELIGHT_GREEN_IQ_INACTIVE");
            };
            MenuLogic.addCriteria('zone.' + hcId, zoneActiveCondition);

            MenuLogic.addCriteria('zone.abwesenheit.' + hcId, zoneActiveCondition);

            MenuLogic.addCriteria('config-zone-' + hcId, zoneActiveCondition);
        }
        for (let zId of this.ioService.deviceInfo().getZoneIds()) {
            let zoneActivatedCondition = () => {
                return (this.ioService.deviceInfo().getZoneById(zId).getRawValue('zone_active') == "ENUM_ZONE_ENABLED_YES");
            }
            MenuLogic.addCriteria('zone.abwesenheit.' + zId, zoneActivatedCondition);
            MenuLogic.addCriteria('zone.' + zId, zoneActivatedCondition);
        }
        MenuLogic.addCriteria('sensor-aktor-test-vr70-3', () => {
            return (this.ioService.deviceInfo().getVR70Count() > 2);
        });
        MenuLogic.addCriteria('sensor-aktor-test-vr70-2', () => {
            return (this.ioService.deviceInfo().getVR70Count() > 1);
        });
        MenuLogic.addCriteria('sensor-aktor-test-vr70-1', () => {
            return (this.ioService.deviceInfo().getVR70Count() >= 1);
        });
        MenuLogic.addCriteria('sensor-aktor-test-vr71', () => {
            return this.config.simulatorConfig.hasVR71();
        });

    }
    applyVentilationFilters() {

        if (!(this.ventConfig.isAvailable()) || this.ventConfig.getValue() == "no") {
            MenuLogic.addCriteria('lueftung.menu.anchor', () => { return false; });
            MenuLogic.addCriteria('waermerueckgewinnung', () => { return false; });
        }
    }
    applySolarSystemFilters() {

        MenuLogic.addCriteria('solarertrag', () => {
            let hasVR71 = this.config.simulatorConfig.hasVR71();
            let vr71_value = this.ioService.deviceInfo().getScale('configuration_vr71').getCurrentValue();
            let solarCylinderCount = this.config.simulatorConfig.solarCylinderCount();

            return ([2, 1].includes(vr71_value) && hasVR71 && solarCylinderCount > 0) || (this.solarConfigured());
        });
        MenuLogic.addCriteria('solarkreis', () => {
            let solarCylinderCount = this.config.simulatorConfig.solarCylinderCount();
            let vr71_value = this.ioService.deviceInfo().getScale('configuration_vr71').getCurrentValue();
            let hasVR71 = this.config.simulatorConfig.hasVR71();
            return ([2, 1].includes(vr71_value) && hasVR71 && solarCylinderCount > 0) || (this.solarConfigured());

        });
        MenuLogic.addCriteria('solarspeicher1', () => {
            let solarCylinderCount = this.config.simulatorConfig.solarCylinderCount();
            let hasVR71 = this.config.simulatorConfig.hasVR71();
            let vr71_value = this.ioService.deviceInfo().getScale('configuration_vr71').getCurrentValue();
            return ([2, 1].includes(vr71_value) && hasVR71 && solarCylinderCount > 0) || (this.solarConfigured());
        });
        MenuLogic.addCriteria('solarspeicher2', () => {
            let solarCylinderCount = this.config.simulatorConfig.solarCylinderCount();
            let vr71_value = this.ioService.deviceInfo().getScale('configuration_vr71').getCurrentValue();
            let hasVR71 = this.config.simulatorConfig.hasVR71();
            return ((vr71_value == 1 && hasVR71 && solarCylinderCount == 2) || (this.solarConfigured() && solarCylinderCount == 2));
        });
        MenuLogic.addCriteria('menuentry-2nd-td', () => {
            let solarCylinderCount = this.config.simulatorConfig.solarCylinderCount();
            let vr71_value = this.ioService.deviceInfo().getScale('configuration_vr71').getCurrentValue();
            let hasVR71 = this.config.simulatorConfig.hasVR71();
            let vr70_value = this.ioService.deviceInfo().getScale('configuration_vr70').getCurrentValue()
            let isTDControl = this.ioService.deviceInfo().getRawValue('configuration_ma_vr71') == 'ENUM_MAVR71_DT_CONTROL';
            //console.log("SolarCylinderCount:" +solarCylinderCount);
            return ([2, 1].includes(vr71_value) && hasVR71 && isTDControl && [2, 1].includes(solarCylinderCount)) || (this.solarConfigured() && vr70_value == 12);
        });

    }
    isMixedCircuit() {
        let hcCount = this.heatingCircuitCount();
        let wizard_zone_option = this.ioService.deviceInfo().getRawValue('wizard_zone_option');
        let mixed_options = ['WIZARD_ZONE_OPTIONS_ENUM3', 'WIZARD_ZONE_OPTIONS_ENUM4', 'WIZARD_ZONE_OPTIONS_ENUM5'];
        if ((hcCount > 1) || (hcCount == 1 && mixed_options.includes(wizard_zone_option))) {
            return true;
        }
        return false;
    }
}